import { useMemo, useEffect, useRef } from "react"
import { useSelector } from 'react-redux'
import { Select } from "@mantine/core"
import BlackTextInput from "app/views/components/inputs/BlackTextInput"
import BlackNumberInput from "app/views/components/inputs/BlackNumberInput"
import BlackDateInput from "app/views/components/inputs/BlackDateInput"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import WarningText from 'app/views/components/warning-text/warning-text'
import { getEntities } from "app/state/ducks/entities/selectors"
import { getFacility } from "app/state/ducks/facilities/selectors"
import { getBusinessDate } from "app/state/ducks/business-date/selectors"
import { LoanParams } from "app/models/loan-params"
import { roundTo, formatDateToUTC } from 'app/utils/util-functions'
import { pikOptions } from 'app/models/dropdown-options'
import { getPikConfig } from '../loans/utilities/pik-config-utils'
import { ReactComponent as ChevronDownIcon } from 'app/styles/chevronDown.svg'
import { roundBaseRate } from 'app/utils/rate-rounding'

type NewLoan = {
    indexRateOption: string
    amount: string
    borrower: string
    startDate: Date | null
    maturityDate: Date | null
    interestBaseRate: string
    interestBaseRateRounding: string
    dayBasis: string
    margin: string
    cas: string
    allInRate: string
    facilityId: string
    currency: string
    // Rate to the facility base currency; '' when they match
    fxRate: string
    isNonProRata: boolean
    rounding: string
    pikPremium: string
    pikInterestPercent: string
    cashInterestPercent: string
    newLoanWithPik: string
}

type MoreLoanOptionsPanelProps = {
    index: number
    loan: NewLoan
    selectedLoans?: LoanParams[]
    onFieldChange: (index: number, field: keyof NewLoan, value: string | Date | null) => void
}

export const formatDateForSubmission = (date: Date | null): string | null => {
    return date ? formatDateToUTC(date) : null
}

export default function MoreLoanOptionsPanel({
    index,
    loan,
    selectedLoans,
    onFieldChange
}: MoreLoanOptionsPanelProps) {
    const entities = useSelector(getEntities)
    const facility = useSelector(getFacility)
    const businessDate = useSelector(getBusinessDate)

    const pikConfig = getPikConfig(facility?.pikOption)
    // The merged loan inherits its currency from the source loans; a rate is
    // only needed (and only accepted by the ledger) when that differs from the
    // facility's base currency.
    const isForeignCurrencyLoan =
        !!facility?.baseCurrency &&
        !!loan.currency &&
        loan.currency !== facility.baseCurrency

    // Use ref to track if initial auto-population has happened for this loan instance
    const hasAutoPopulated = useRef(false)

    // Reset auto-population flag when loan is reset (empty fields) or index rate option changes
    useEffect(() => {
        // If the loan appears to be reset (key fields are empty), reset the auto-population flag
        if (!loan.margin && !loan.dayBasis && !loan.cas) {
            hasAutoPopulated.current = false
        }
    }, [loan.margin, loan.dayBasis, loan.cas])

    // Reset auto-population flag when index rate option changes to allow re-population
    useEffect(() => {
        hasAutoPopulated.current = false
    }, [loan.indexRateOption])

    // Extract borrower info from selected loans (they're all be required to have the same borrower)
    const borrowerInfo = useMemo(() => {
        if (!selectedLoans?.length || !entities) {
            return { id: '', name: '' }
        }

        const borrowerId = selectedLoans[0].borrower?.id || ''
        const borrowerEntity = entities.find(entity => entity.id === borrowerId)
        const borrowerName = borrowerEntity?.entityName || ''

        return { id: borrowerId, name: borrowerName }
    }, [selectedLoans, entities])

    // Extract facility IRO values based on selected index rate option
    const facilityIroValues = useMemo(() => {
        if (!facility?.iroValues || !loan.indexRateOption) {
            return null
        }

        // Find the iroValue that matches the selected index rate option
        const matchingIro = facility.iroValues.find(iro =>
            iro.indexOption?.id === loan.indexRateOption
        )

        return matchingIro || null
    }, [facility, loan.indexRateOption])

    // Update form with borrower ID when selectedLoans changes
    useEffect(() => {
        if (borrowerInfo.id && onFieldChange && borrowerInfo.id !== loan.borrower) {
            onFieldChange(index, 'borrower', borrowerInfo.id)
        }
    }, [borrowerInfo.id, onFieldChange, index, loan.borrower])

    // Set default start date to business date
    useEffect(() => {
        if (businessDate && !loan.startDate && onFieldChange) {
            onFieldChange(index, 'startDate', new Date(businessDate))
        }
    }, [businessDate, loan.startDate, onFieldChange, index])

    // Auto-populate form fields from facility IRO values ONCE when facility data becomes available
    useEffect(() => {
        if (!facilityIroValues || !onFieldChange || hasAutoPopulated.current) {
            return
        }

        // Only auto-populate if fields are empty (indicating this is initial load, not user interaction)
        let shouldAutoPopulate = false

        // Update margin if available and field is empty
        if (facilityIroValues.margin !== undefined &&
            facilityIroValues.margin !== null &&
            !loan.margin) {
            const marginValue = Number(facilityIroValues.margin).toString()
            onFieldChange(index, 'margin', marginValue)
            shouldAutoPopulate = true
        }

        // Update day basis if available and field is empty
        if (facilityIroValues.dayBasis && !loan.dayBasis) {
            const displayDayBasis = facilityIroValues.dayBasis.replace('_', '/')
            onFieldChange(index, 'dayBasis', displayDayBasis)
            shouldAutoPopulate = true
        }

        // Mark as auto-populated so we don't override user changes
        if (shouldAutoPopulate) {
            hasAutoPopulated.current = true
        }
    }, [facilityIroValues, onFieldChange, index, loan.margin, loan.dayBasis, loan.cas])

    // Calculate Interest Base Rate (Rounding) based on Interest Base Rate and facility IRO values
    useEffect(() => {
        const interestBaseRateValue = parseFloat(loan.interestBaseRate) || 0;
        if (interestBaseRateValue && facilityIroValues?.rounding) {
            const roundedValue = roundTo(interestBaseRateValue, facilityIroValues.rounding);
            if (roundedValue !== undefined) {
                // Convert to number and format to 5 decimal places to match precision of other fields
                const numericValue = typeof roundedValue === 'string' ? parseFloat(roundedValue) : roundedValue;
                const formattedValue = `${numericValue.toFixed(5)}%`;
                // Only update if the value has actually changed
                if (loan.interestBaseRateRounding !== formattedValue) {
                    onFieldChange(index, 'interestBaseRateRounding', formattedValue);
                }
            }
        } else if (!interestBaseRateValue && loan.interestBaseRateRounding !== '') {
            onFieldChange(index, 'interestBaseRateRounding', '');
        }
    }, [loan.interestBaseRate, facilityIroValues, onFieldChange, index, loan.interestBaseRateRounding]);

    // Calculate All-In Rate as ROUNDED Interest Base Rate + Margin + CAS + PIK Premium.
    // Derived from the base rate and the IRO's rounding mode on every run, not read
    // back from the formatted rounding field, which can lag a render behind.
    useEffect(() => {
        const roundedBaseRate = roundBaseRate(parseFloat(loan.interestBaseRate) || 0, facilityIroValues?.rounding);
        const margin = parseFloat(loan.margin) || 0;
        const cas = parseFloat(loan.cas) || 0;
        const pikPremium = parseFloat(loan.pikPremium) || 0;

        const allInRate = roundedBaseRate + margin + cas + pikPremium;
        // Format to 5 decimal places consistently with the other fields
        const formattedValue = `${(Math.round(allInRate * 100000) / 100000).toFixed(5)}%`;

        // Only update if the value has actually changed
        if (loan.allInRate !== formattedValue) {
            onFieldChange(index, 'allInRate', formattedValue);
        }
    }, [loan.interestBaseRate, facilityIroValues?.rounding, loan.margin, loan.cas, loan.pikPremium, onFieldChange, index, loan.allInRate]);

    const pikToggleOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
    ]

    const pikPercentagesValid = useMemo(() => {
        if (!pikConfig.showNewLoanPikFields && !(pikConfig.showNewLoanPikToggle && loan.newLoanWithPik === 'Yes')) {
            return true
        }

        const pikPercent = parseFloat(loan.pikInterestPercent) || 0
        const cashPercent = parseFloat(loan.cashInterestPercent) || 0
        const allInRate = parseFloat(loan.allInRate?.replace('%', '')) || 0

        const sum = cashPercent + pikPercent
        const tolerance = 0.01
        const diff = Math.round(Math.abs(sum - allInRate) * 100) / 100
        return diff < tolerance
    }, [loan.cashInterestPercent, loan.pikInterestPercent, loan.allInRate, pikConfig.showNewLoanPikFields, pikConfig.showNewLoanPikToggle, loan.newLoanWithPik])

    return (
        <div className="more-loan-options-panel">

            {/* First row */}
            <div className="more-loan-options-row">
                <BlackTextInput
                    label="Borrower"
                    value={borrowerInfo.name}
                    onChange={() => { }} // Read-only field
                    readOnly={true}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
                <BlackDateInput
                    label="Start Date"
                    value={loan.startDate}
                    onChange={(value) => onFieldChange(index, 'startDate', value)}
                    disabled={true}
                />
                <BlackDateInput
                    label="Maturity Date"
                    value={loan.maturityDate}
                    onChange={(value) => onFieldChange(index, 'maturityDate', value)}
                />
            </div>

            {/* Second row */}
            <div className="more-loan-options-row">
                <BlackNumberInput
                    label="Interest Base Rate"
                    value={loan.interestBaseRate ? parseFloat(loan.interestBaseRate) : ''}
                    onChange={(value) => onFieldChange(index, 'interestBaseRate', value?.toString() || '')}
                    precision={5}
                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
                <BlackTextInput
                    label="Interest Base Rate (Rounding)"
                    value={loan.interestBaseRateRounding}
                    onChange={() => { }} // Read-only calculated field
                    readOnly={true}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
                <BlackTextInput
                    label="Day Basis"
                    value={loan.dayBasis}
                    onChange={() => { }} // Read-only field
                    readOnly={true}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
            </div>

            {/* Third row */}
            <div className="more-loan-options-row">
                <BlackNumberInput
                    label="Margin"
                    value={loan.margin ? parseFloat(loan.margin) : ''}
                    onChange={(value) => onFieldChange(index, 'margin', value?.toString() || '')}
                    precision={5}
                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
                <BlackNumberInput
                    label="CAS"
                    value={loan.cas ? parseFloat(loan.cas) : ''}
                    onChange={(value) => onFieldChange(index, 'cas', value?.toString() || '')}
                    precision={5}
                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
                <BlackTextInput
                    label="All-In Rate"
                    value={loan.allInRate}
                    onChange={() => { }} // Read-only calculated field
                    readOnly={true}
                    classNames={{ label: "black-input-label-with-padding" }}
                />
            </div>

            {isForeignCurrencyLoan && (
                <div className="more-loan-options-row">
                    <BlackNumberInput
                        label="FX Rate"
                        description={`${loan.currency} → ${facility?.baseCurrency}`}
                        placeholder="Enter FX rate"
                        value={loan.fxRate ? parseFloat(loan.fxRate) : ''}
                        onChange={(value) => onFieldChange(index, 'fxRate', value?.toString() || '')}
                        precision={6}
                        min={0}
                        classNames={{ label: "black-input-label-with-padding" }}
                    />
                </div>
            )}

            {pikConfig.showPikConfiguration && (
                <>
                    <div className="more-loan-options-row">
                        <Select
                            label="PIK Option"
                            data={pikOptions}
                            value={facility?.pikOption}
                            readOnly
                            disabled
                            styles={{
                                input: {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed',
                                },
                                label: {
                                    padding: '10px 0px'
                                }
                            }}
                        />
                        {pikConfig.showNewLoanPikToggle && (
                            <BlackSelectInput
                                label="New Loan PIK (ON/OFF)"
                                placeholder="Select"
                                data={pikToggleOptions}
                                value={loan.newLoanWithPik}
                                onChange={(value) => onFieldChange(index, 'newLoanWithPik', value || '')}
                                classNames={{
                                    input: "merge-form-editable-select"
                                }}
                                styles={{
                                    label: {
                                        padding: '10px 0px'
                                    },
                                    rightSection: {
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%'
                                    }
                                }}
                                rightSection={<ChevronDownIcon />}
                            />
                        )}
                    </div>

                    {(pikConfig.showNewLoanPikFields || (pikConfig.showNewLoanPikToggle && loan.newLoanWithPik === 'Yes')) && (
                        <>
                            <div className="more-loan-options-row">
                                <BlackNumberInput
                                    label="PIK Premium"
                                    value={loan.pikPremium ? parseFloat(loan.pikPremium) : ''}
                                    onChange={(value) => onFieldChange(index, 'pikPremium', value?.toString() || '')}
                                    precision={5}
                                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                                    classNames={{ label: "black-input-label-with-padding" }}
                                />
                                <BlackNumberInput
                                    label="PIK Interest %"
                                    value={loan.pikInterestPercent ? parseFloat(loan.pikInterestPercent) : ''}
                                    onChange={(value) => onFieldChange(index, 'pikInterestPercent', value?.toString() || '')}
                                    precision={5}
                                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                                    classNames={{ label: "black-input-label-with-padding" }}
                                />
                                <BlackNumberInput
                                    label="Cash Interest %"
                                    value={loan.cashInterestPercent ? parseFloat(loan.cashInterestPercent) : ''}
                                    onChange={(value) => onFieldChange(index, 'cashInterestPercent', value?.toString() || '')}
                                    precision={5}
                                    formatter={(value) => !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'}
                                    parser={(value) => value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''}
                                    classNames={{ label: "black-input-label-with-padding" }}
                                />
                            </div>

                            {!pikPercentagesValid && (
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <WarningText
                                        type="error"
                                        text="PIK Cash % + PIK Interest % must equal All In Rate"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}
